// Displays the flattened entry list and routes row-level edit and hover interactions.
import React from "react";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { appScrollbarSx } from "../../../lib/scrollbarSx";
import type { EntryListCommentPart } from "../types";

export interface EntryListTableRow {
  sequence: number;
  displayLetter: string;
  displayReference: string;
  letter: string;
  isBeginningBalance: boolean;
  account: string;
  debitAmount?: number;
  creditAmount?: number;
  isCredit: boolean;
  comment?: string;
}

interface EntryListTableProps {
  entryListRows: EntryListTableRow[];
  shouldShowRelatedTotalsPanel: boolean;
  highlightedLetter?: string | null;
  onLineItemLabelHover?: (letter: string | null) => void;
  formatAmount: (value: number) => string;
  resolveComment: (row: EntryListTableRow) => string;
  resolveCommentParts?: (row: EntryListTableRow) => EntryListCommentPart[];
  onAmountClick?: (reference: string, signedAmount: number) => void;
  onBeginningBalanceClick?: (accountName: string, signedAmount: number) => void;
  onCommentClick?: (reference: string, signedAmount: number, resolvedComment: string) => void;
  onTopLevelCommentClick?: (reference: string, commentText: string) => void;
  onEmptyCommentClick?: (reference: string) => void;
}

export default function EntryListTable({
  entryListRows,
  shouldShowRelatedTotalsPanel,
  highlightedLetter = null,
  onLineItemLabelHover,
  formatAmount,
  resolveComment,
  resolveCommentParts,
  onAmountClick,
  onBeginningBalanceClick,
  onCommentClick,
  onTopLevelCommentClick,
  onEmptyCommentClick,
}: EntryListTableProps) {
  const renderInteractiveAmount = (
    row: EntryListTableRow,
    side: "debit" | "credit",
    amount: number,
  ) => {
    const isClickable = row.isBeginningBalance ? !!onBeginningBalanceClick : !!onAmountClick;
    const signedAmount = side === "debit" ? -Math.abs(amount) : Math.abs(amount);

    const amountContent = (
      <Box
        component={isClickable ? "button" : "span"}
        type={isClickable ? "button" : undefined}
        aria-label={isClickable
          ? (row.isBeginningBalance
            ? `Edit beginning balance ${row.account} ${formatAmount(amount)}`
            : `Edit ${side} ${row.displayReference} ${formatAmount(amount)}`)
          : undefined}
        onMouseDown={isClickable ? (event) => event.preventDefault() : undefined}
        onClick={isClickable
          ? () => {
            if (row.isBeginningBalance) {
              onBeginningBalanceClick?.(row.account, signedAmount);
              return;
            }

            onAmountClick?.(row.displayReference, signedAmount);
          }
          : undefined}
        sx={{
          border: 0,
          p: 0,
          m: 0,
          backgroundColor: "transparent",
          borderRadius: 0.75,
          cursor: isClickable ? "pointer" : "default",
          transition: "background-color 120ms ease-in-out",
          color: "inherit",
          font: "inherit",
          lineHeight: "inherit",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "flex-end",
          width: "100%",
          px: 0.25,
          "&:hover": isClickable ? { backgroundColor: "rgba(0, 0, 0, 0.04)" } : undefined,
        }}
      >
        {formatAmount(amount)}
      </Box>
    );

    if (!isClickable) {
      return amountContent;
    }

    return amountContent;
  };

  const renderInteractiveCommentPart = (row: EntryListTableRow, part: EntryListCommentPart) => {
    const baseReference = row.displayReference.split(".")[0];
    const hasAmount = row.debitAmount !== undefined || row.creditAmount !== undefined;
    const signedAmount = row.debitAmount !== undefined
      ? -Math.abs(row.debitAmount)
      : (row.creditAmount !== undefined ? Math.abs(row.creditAmount) : 0);
    const isTopLevelPart = part.kind === "top-level";
    const canEditTopLevel = !row.isBeginningBalance && !!onTopLevelCommentClick && part.text.trim().length > 0;
    const canEditLineItem = !row.isBeginningBalance && !!onCommentClick && part.text.trim().length > 0 && hasAmount;
    const isClickable = isTopLevelPart ? canEditTopLevel : canEditLineItem;

    const label = isTopLevelPart
      ? `Edit top-level comment ${baseReference}`
      : `Edit comment ${row.displayReference}`;

    const commentContent = (
      <Box
        component={isClickable ? "button" : "span"}
        type={isClickable ? "button" : undefined}
        aria-label={isClickable ? label : undefined}
        onMouseDown={isClickable ? (event) => event.preventDefault() : undefined}
        onClick={isClickable
          ? () => {
            if (isTopLevelPart) {
              onTopLevelCommentClick?.(baseReference, part.text);
              return;
            }

            onCommentClick?.(row.displayReference, signedAmount, part.text);
          }
          : undefined}
        sx={{
          border: 0,
          p: 0,
          m: 0,
          backgroundColor: "transparent",
          borderRadius: 0.75,
          cursor: isClickable ? "pointer" : "default",
          transition: "background-color 120ms ease-in-out",
          color: "inherit",
          font: "inherit",
          lineHeight: "inherit",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "flex-start",
          minWidth: 0,
          maxWidth: "100%",
          px: 0.25,
          textAlign: "left",
          "&:hover": isClickable ? { backgroundColor: "rgba(0, 0, 0, 0.04)" } : undefined,
        }}
      >
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "100%",
          }}
        >
          {part.text}
        </Box>
      </Box>
    );

    if (!isClickable) {
      return commentContent;
    }

    return (
      <Tooltip title={isTopLevelPart ? "Click top-level comment to edit" : "Click comment to edit"} placement="top" arrow describeChild>
        {commentContent}
      </Tooltip>
    );
  };

  const renderInteractiveComment = (row: EntryListTableRow, resolvedComment: string) => {
    const parts = resolveCommentParts?.(row)
      ?? (resolvedComment ? [{ text: resolvedComment, kind: "line-item" as const }] : []);

    const hasAmount = row.debitAmount !== undefined || row.creditAmount !== undefined;
    const canAddComment = !row.isBeginningBalance && !!onEmptyCommentClick && hasAmount;

    if (parts.length === 0) {
      if (!canAddComment) {
        return null;
      }

      return (
        <Box
          component="button"
          type="button"
          className="entry-list-add-comment-hint"
          aria-label={`Add comment for ${row.displayReference}`}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onEmptyCommentClick?.(row.displayReference)}
          sx={{
            border: 0,
            m: 0,
            width: "100%",
            px: 0.75,
            py: 0.25,
            borderRadius: 0.75,
            backgroundColor: "rgba(0, 0, 0, 0)",
            color: (theme) => alpha(theme.palette.text.primary, 0.72),
            cursor: "pointer",
            textAlign: "left",
            font: "inherit",
            lineHeight: "inherit",
            opacity: 0,
            transition: "opacity 120ms ease-in-out, background-color 120ms ease-in-out",
            "&:hover": {
              opacity: 1,
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
            "&:focus-visible": {
              opacity: 1,
              backgroundColor: "rgba(0, 0, 0, 0.06)",
              outline: "none",
            },
          }}
        >
          Add comment
        </Box>
      );
    }

    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", minWidth: 0, maxWidth: "100%" }}>
        {parts.map((part, index) => (
          <React.Fragment key={`${row.sequence}-${part.kind}-${index}`}>
            {index > 0 ? <Box component="span" sx={{ px: 0.25 }}>/</Box> : null}
            {renderInteractiveCommentPart(row, part)}
          </React.Fragment>
        ))}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        minHeight: 0,
        display: "grid",
        gridTemplateColumns: shouldShowRelatedTotalsPanel ? "1fr 200px" : "1fr",
      }}
    >
      <Box
        className="export-expand-height"
        sx={{
          px: 0,
          py: 0,
          maxHeight: 180,
          borderTop: "1px solid",
          borderColor: (theme) => (
            theme.palette.mode === "dark"
              ? alpha(theme.palette.common.white, 0.22)
              : alpha(theme.palette.common.black, 0.12)
          ),
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Table
          size="small"
          sx={{
            width: "100%",
            tableLayout: "fixed",
            borderBottom: "1px solid",
            borderColor: (theme) => (
              theme.palette.mode === "dark"
                ? alpha(theme.palette.common.white, 0.2)
                : alpha(theme.palette.common.black, 0.08)
            ),
          }}
        >
          <colgroup>
            <col style={{ width: "72px" }} />
            <col style={{ width: "128px" }} />
            <col style={{ width: "110px" }} />
            <col style={{ width: "110px" }} />
            <col />
          </colgroup>

          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: "nowrap", borderBottom: "none" }} />
              <TableCell sx={{ whiteSpace: "nowrap", borderBottom: "none", fontWeight: 700 }}>Account</TableCell>
              <TableCell align="right" sx={{ whiteSpace: "nowrap", borderBottom: "none", fontWeight: 700 }}>Debit</TableCell>
              <TableCell align="right" sx={{ whiteSpace: "nowrap", borderBottom: "none", fontWeight: 700 }}>Credit</TableCell>
              <TableCell sx={{ borderBottom: "none", fontWeight: 700 }}>Comment</TableCell>
            </TableRow>
          </TableHead>
        </Table>

        <TableContainer
          className="export-expand-scroll"
          sx={{
            maxHeight: 118,
            overflowY: "auto",
            ...appScrollbarSx,
          }}
        >
          <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: "72px" }} />
              <col style={{ width: "128px" }} />
              <col style={{ width: "110px" }} />
              <col style={{ width: "110px" }} />
              <col />
            </colgroup>

            <TableHead>
              <TableRow sx={{ display: "none" }} />
            </TableHead>

            <TableBody>
              {entryListRows.flatMap((row, rowIndex) => {
                const currentLetterGroup = row.displayLetter.split(".")[0].toLowerCase();
                const nextLetterGroup = entryListRows[rowIndex + 1]?.displayLetter.split(".")[0].toLowerCase();
                const isEndOfLetterGroup = !!nextLetterGroup && nextLetterGroup !== currentLetterGroup;

                const rows: React.ReactNode[] = [
                  <TableRow
                    key={row.sequence}
                    sx={{
                      backgroundColor: !row.isBeginningBalance && highlightedLetter === row.letter
                        ? "rgba(25, 118, 210, 0.08)"
                        : "transparent",
                    }}
                  >
                    <TableCell
                      sx={{
                        whiteSpace: "nowrap",
                        borderBottom: "none",
                        py: 0,
                        fontStyle: row.isBeginningBalance ? "italic" : "normal",
                        cursor: !row.isBeginningBalance ? "pointer" : "default",
                      }}
                      onMouseEnter={!row.isBeginningBalance ? () => onLineItemLabelHover?.(row.letter) : undefined}
                      onMouseLeave={!row.isBeginningBalance ? () => onLineItemLabelHover?.(null) : undefined}
                    >
                      {row.displayLetter}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", borderBottom: "none", pl: row.isCredit ? 3 : 2, py: 0, fontStyle: row.isBeginningBalance ? "italic" : "normal" }}>{row.account}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap", borderBottom: "none", py: 0, fontStyle: row.isBeginningBalance ? "italic" : "normal" }}>
                      {row.debitAmount !== undefined ? renderInteractiveAmount(row, "debit", row.debitAmount) : ""}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap", borderBottom: "none", py: 0, fontStyle: row.isBeginningBalance ? "italic" : "normal" }}>
                      {row.creditAmount !== undefined ? renderInteractiveAmount(row, "credit", row.creditAmount) : ""}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: "none",
                        py: 0,
                        fontStyle: row.isBeginningBalance ? "italic" : "normal",
                        "&:hover .entry-list-add-comment-hint": {
                          opacity: 1,
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                    >
                      {renderInteractiveComment(row, resolveComment(row))}
                    </TableCell>
                  </TableRow>,
                ];

                if (isEndOfLetterGroup) {
                  rows.push(
                    <TableRow key={`spacer-${row.sequence}`}>
                      <TableCell colSpan={5} sx={{ borderBottom: "none", height: 4, p: 0 }} />
                    </TableRow>,
                  );
                }

                return rows;
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {shouldShowRelatedTotalsPanel ? (
        <Box
          className="export-hide-divider"
          sx={{
            borderLeft: "1px solid",
            borderColor: (theme) => (
              theme.palette.mode === "dark"
                ? alpha(theme.palette.common.white, 0.22)
                : alpha(theme.palette.common.black, 0.12)
            ),
          }}
        />
      ) : null}
    </Box>
  );
}
